// Seed handoff into the offer builder. Entry points (the have-list hover and
// the trade suggestions surfaces) write a full-bundle seed here, then navigate
// to /trade-offers/new/:username; OfferBuilderContext consumes it exactly once.

import * as Sentry from '@sentry/react';
import { OfferBuilderSeed, OfferLineItem } from 'types/interfaces/tradeOffer';
import { TradeSuggestion, TradeSuggestionItem } from 'types/interfaces/tradeSuggestion';
import { finishFields, haveFinishFromRow } from 'utils/finishes';

const SEED_KEY = 'trade_offer_builder_seed';

// The suggestion item shape is the TradeOfferItem response shape by contract,
// so every display field maps 1:1. ownedQuantity clamps the builder's stepper
// at the suggested quantity: the suggestion allocated exactly these copies
// under the fan-out cap, so the seed must not invite raising it.
//
// Returns null when the have row backing the line is gone. The wire types
// userMTGCardHaveId nullable because the row can be deleted after generation,
// and validate rejects a null one for every item on both sides
// (TradeSuggestionService validate: a missing row fails stillValid), so on the
// real path — seed only after a successful validate — this cannot happen.
const suggestionItemToLineItem = (item: TradeSuggestionItem): OfferLineItem | null => {
  if (item.userMTGCardHaveId == null) return null;
  return {
    userMTGCardHaveId: item.userMTGCardHaveId,
    quantity: item.quantity,
    ownedQuantity: item.quantity,
    unitPricePoints: item.lockedPricePoints,
    ...finishFields(haveFinishFromRow(item)),
    condition: item.condition,
    card: item.card,
  };
};

/**
 * Full-bundle seed from a validated suggestion. Carries EVERY item on both
 * sides — the suggestion card may collapse long bundles visually, but the seed
 * is never truncated — plus the suggestionId so a proposed offer releases the
 * suggestion's allocation server-side.
 *
 * Returns null rather than a partial seed when any line has lost its have row.
 * All-or-nothing is the point: dropping the line would open the builder with a
 * smaller bundle than the card displayed, silently. Callers show the stale
 * state instead.
 */
export const buildSeedFromSuggestion = (
  suggestion: TradeSuggestion
): OfferBuilderSeed | null => {
  const youSend = suggestion.youSend.map(suggestionItemToLineItem);
  const theySend = suggestion.theySend.map(suggestionItemToLineItem);

  const missing = [...youSend, ...theySend].filter((line) => line === null).length;
  if (missing > 0) {
    // Unreachable if validate is working: reaching here means validate passed
    // and a have row disappeared between that response and this call. Surface
    // it rather than let it read as an ordinary stale suggestion (Eric, 2026-08-03).
    const message =
      `Suggestion ${suggestion.id} seeded with ${missing} item(s) missing ` +
      `userMTGCardHaveId after a passing validate; refusing to seed a partial bundle.`;
    console.error(message);
    Sentry.captureMessage(message, {
      level: 'error',
      tags: { feature: 'trade-suggestions', anomaly: 'seed_missing_have_id' },
      extra: {
        suggestionId: suggestion.id,
        missingCount: missing,
        youSendCount: suggestion.youSend.length,
        theySendCount: suggestion.theySend.length,
      },
    });
    return null;
  }

  return {
    counterparty: {
      id: suggestion.counterparty.id,
      username: suggestion.counterparty.username,
    },
    youSend: youSend as OfferLineItem[],
    theySend: theySend as OfferLineItem[],
    suggestionId: suggestion.id,
  };
};

export const writeOfferBuilderSeed = (seed: OfferBuilderSeed): void => {
  try {
    sessionStorage.setItem(SEED_KEY, JSON.stringify(seed));
  } catch (e) {
    console.error('Failed to write offer builder seed:', e);
  }
};

export const readOfferBuilderSeed = (): OfferBuilderSeed | null => {
  try {
    const raw = sessionStorage.getItem(SEED_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as OfferBuilderSeed;
    if (!parsed || !parsed.counterparty || typeof parsed.counterparty.id !== 'number') {
      return null;
    }
    return {
      counterparty: parsed.counterparty,
      youSend: Array.isArray(parsed.youSend) ? parsed.youSend : [],
      theySend: Array.isArray(parsed.theySend) ? parsed.theySend : [],
      suggestionId: typeof parsed.suggestionId === 'string' ? parsed.suggestionId : undefined,
    };
  } catch (e) {
    console.error('Failed to read offer builder seed:', e);
    return null;
  }
};

export const clearOfferBuilderSeed = (): void => {
  try {
    sessionStorage.removeItem(SEED_KEY);
  } catch {
    // sessionStorage unavailable — nothing to clear
  }
};
